import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ROLES, ROLE_LABELS, type Permission, type Role } from "@xop/dashboard-shared";
import { useState } from "react";
import type { JSX } from "react";

import { ApiError } from "../../../lib/api.js";
import { PermissionEditor } from "./PermissionEditor.js";

export function InviteUserDialog({
  isOpen,
  onClose,
  onSubmit,
  canSetOverrides,
  error,
  isSubmitting,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (input: {
    email: string;
    name?: string;
    dashboard_role: Role;
    permission_overrides: Permission[];
  }) => Promise<void>;
  canSetOverrides: boolean;
  error: ApiError | null;
  isSubmitting: boolean;
}): JSX.Element {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("sales_manager");
  const [overrides, setOverrides] = useState<Permission[]>([]);
  const [showOverrides, setShowOverrides] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (open || isSubmitting) return;
    setEmail("");
    setName("");
    setRole("sales_manager");
    setOverrides([]);
    setShowOverrides(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    const trimmedName = name.trim();
    try {
      await onSubmit({
        email: trimmed,
        ...(trimmedName ? { name: trimmedName } : {}),
        dashboard_role: role,
        permission_overrides: overrides,
      });
      // Mutation success — close + reset form.
      setEmail("");
      setName("");
      setRole("sales_manager");
      setOverrides([]);
      setShowOverrides(false);
      onClose();
    } catch {
      // Caller surfaces the ApiError via the `error` prop; keep the
      // dialog open so the admin can fix and retry.
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => handleOpenChange(false)}
      fullWidth
      PaperProps={{ sx: { maxWidth: 520 } }}
    >
      <DialogTitle>Invite a new dashboard user</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 14 }}>
          Pre-provision a role for an email that hasn't signed in yet.
          The dashboard remembers the role + overrides and applies them
          the first time that email signs in via the Support Center. The
          login credentials still live in the Support Center — invite
          here, then create the matching credential there.
        </DialogContentText>

        <Box
          component="form"
          id="invite-user-form"
          onSubmit={handleSubmit}
          sx={{ display: "grid", gap: 2, mt: 2 }}
        >
          <TextField
            autoFocus
            type="email"
            required
            size="small"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="sales-rep@xop.test"
          />

          <TextField
            size="small"
            label="Full name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            helperText="Used to greet them by name in the invitation email."
          />

          <Box sx={{ display: "grid", gap: 0.5 }}>
            <Typography
              component="label"
              htmlFor="invite-role-select"
              sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}
            >
              Dashboard role
            </Typography>
            {/* Native <select> (not MUI Select) so the test can drive it via
                userEvent.selectOptions on the labelled control. */}
            <Box
              component="select"
              id="invite-role-select"
              value={role}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setRole(e.target.value as Role)}
              sx={{
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                bgcolor: "background.default",
                color: "text.primary",
                px: 1,
                py: 0.75,
                fontSize: 14,
                cursor: "pointer",
                "&:hover": { borderColor: "text.disabled" },
              }}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </Box>
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
              Picks the default permission bundle. You can layer per-user
              overrides below.
            </Typography>
          </Box>

          {canSetOverrides && (
            <Box sx={{ display: "grid", gap: 1 }}>
              <Box
                component="button"
                type="button"
                onClick={() => setShowOverrides((v) => !v)}
                sx={{
                  justifySelf: "start",
                  border: 0,
                  bgcolor: "transparent",
                  p: 0,
                  fontSize: 12,
                  fontWeight: 500,
                  color: "text.secondary",
                  cursor: "pointer",
                  font: "inherit",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {showOverrides ? "Hide" : "Add"} per-permission overrides
              </Box>
              {showOverrides && (
                <PermissionEditor
                  current={overrides}
                  onChange={setOverrides}
                />
              )}
            </Box>
          )}

          {error && (
            <Box
              role="alert"
              sx={{
                borderRadius: 1,
                border: 1,
                borderColor: (t) => alpha(t.palette.error.main, 0.4),
                bgcolor: (t) => alpha(t.palette.error.main, 0.12),
                px: 1.5,
                py: 1,
                fontSize: 12,
                color: "error.main",
              }}
            >
              {error.message}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => handleOpenChange(false)}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="invite-user-form"
          variant="contained"
          disabled={isSubmitting || !email.trim()}
        >
          {isSubmitting ? "Inviting…" : "Send invite"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
